import React, { useEffect, useRef, useState } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import './Network.css';

/* ── Iconos SVG para sensores ── */
const SENSOR_ICONS = {
  temp: (color) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 4v10.54a4 4 0 11-4 0V4a2 2 0 014 0z"/>
    </svg>
  ),
  humidity: (color) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0L12 2.69z"/>
    </svg>
  ),
  wind: (color) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.7 7.7a2.5 2.5 0 111.8 4.3H2M9.6 4.6a2 2 0 113 2.4H2M12.6 19.4a2 2 0 10-3-2.4H2"/>
    </svg>
  ),
  solar: (color) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    </svg>
  ),
  rain: (color) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 14.899A7 7 0 1115.71 8h1.79a4.5 4.5 0 012.5 8.242"/>
      <path d="M16 14v6M8 14v6M12 16v6"/>
    </svg>
  ),
  soil: (color) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 22h20M6 18v4M10 14v8M14 10v12M18 6v16"/>
    </svg>
  ),
};

/* ── Config de sensores · paleta Steel Blue ──
   Los colores se asignan por CATEGORÍA SEMÁNTICA, no aleatoriamente:
   · warning  → temperatura, radiación solar  (magnitudes "calientes")
   · info     → humedad, lluvia                (magnitudes hídricas/cyan)
   · steel    → viento                         (magnitud neutra/cinética)
   · accent   → suelo                          (magnitud vegetal/orgánica)
   Todos los valores referencian la paleta xagro (xAgro.tokens.css).
*/
const SENSOR_CONFIG = [
  { icon: 'temp',     color: '#c08a3e', valueKey: 'sensor_1_value', labelKey: 'sensor_1_label' }, // warning
  { icon: 'humidity', color: '#2f8a8a', valueKey: 'sensor_2_value', labelKey: 'sensor_2_label' }, // info
  { icon: 'wind',     color: '#5e7d99', valueKey: 'sensor_3_value', labelKey: 'sensor_3_label' }, // steel-500
  { icon: 'solar',    color: '#c08a3e', valueKey: 'sensor_4_value', labelKey: 'sensor_4_label' }, // warning
  { icon: 'rain',     color: '#2f8a8a', valueKey: 'sensor_5_value', labelKey: 'sensor_5_label' }, // info
  { icon: 'soil',     color: '#4f9d6f', valueKey: 'sensor_6_value', labelKey: 'sensor_6_label' }, // accent
];

/* Colores específicos para los 3 callouts sobre la foto.
   Usamos los tres acentos más diferenciables para legibilidad sobre imagen. */
const CALLOUT_COLORS = ['#c08a3e', '#2f8a8a', '#4f9d6f'];

/* ── Chip de sensor ── */
const SensorChip = ({ icon, color, value, label }) => {
  const renderIcon = SENSOR_ICONS[icon];
  return (
    <div className="xAgro-network-sensor-chip">
      <div
        className="xAgro-network-sensor-icon"
        style={{
          background: `${color}1F`,   /* 12% opacidad — visible pero sutil sobre bg claro */
          borderColor: `${color}33`,  /* 20% — borde apenas perceptible */
        }}
      >
        {renderIcon && renderIcon(color)}
      </div>
      <div className="xAgro-network-sensor-data">
        <span className="xAgro-network-sensor-value">{value}</span>
        <span className="xAgro-network-sensor-label">{label}</span>
      </div>
    </div>
  );
};

/* ── Componente principal ── */
const Network = function Network({ data }) {
  const [t] = useTranslation('common');
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const title = t(`${data}.title`);
  const text  = t(`${data}.text`);
  const badge = t(`${data}.badge`);
  const cta   = t(`${data}.cta`);
  const live  = t(`${data}.live`);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('xAgroStations');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.hash = '#xAgroStations';
    }
  };

  return (
    <section
      id="xAgroNetwork"
      className="xAgro-network-section"
      ref={sectionRef}
    >
      <div className="xAgro-network-wrapper">
        <div className="row xAgro-network-row">

          {/* ── Columna izquierda: imagen con overlays ── */}
          <div
            className="col-12 col-md-6 xAgro-network-image-col"
            data-visible={visible}
          >
            <div className="xAgro-network-image-frame">
              <div id="xAgroNetworkImage" className="xAgro-network-image" />

              {/* Badge "EN VIVO" sobre la imagen */}
              <div className="xAgro-network-live-badge" data-visible={visible}>
                <span className="xAgro-network-live-dot-wrapper" aria-hidden="true">
                  <span className="xAgro-network-live-dot-ring" />
                  <span className="xAgro-network-live-dot" />
                </span>
                {live}
              </div>

              {/* Callouts de datos sobre la foto — colores Steel Blue */}
              <div className="xAgro-network-callout xAgro-network-callout-1" data-visible={visible}>
                <span className="xAgro-network-callout-dot" style={{ background: CALLOUT_COLORS[0] }} />
                <span className="xAgro-network-callout-text">{t(`${data}.sensor_1_value`)}</span>
              </div>
              <div className="xAgro-network-callout xAgro-network-callout-2" data-visible={visible}>
                <span className="xAgro-network-callout-dot" style={{ background: CALLOUT_COLORS[1] }} />
                <span className="xAgro-network-callout-text">{t(`${data}.sensor_2_value`)}</span>
              </div>
              <div className="xAgro-network-callout xAgro-network-callout-3" data-visible={visible}>
                <span className="xAgro-network-callout-dot" style={{ background: CALLOUT_COLORS[2] }} />
                <span className="xAgro-network-callout-text">{t(`${data}.sensor_3_value`)}</span>
              </div>

              <div className="xAgro-network-image-fade" />
            </div>
          </div>

          {/* ── Columna derecha: contenido ── */}
          <div className="col-12 col-md-6 xAgro-network-content-col">

            {/* Badge */}
            <div className="xAgro-network-badge" data-visible={visible}>
              <span className="xAgro-network-badge-dot" />
              {badge}
            </div>

            {/* Título */}
            <h1 className="xAgro-network-title" data-visible={visible}>
              {title}
            </h1>

            {/* Descripción */}
            <p className="xAgro-network-text" data-visible={visible}>
              {text}
            </p>

            {/* Grid de sensores */}
            <div className="xAgro-network-sensors" data-visible={visible}>
              {SENSOR_CONFIG.map((s, i) => (
                <SensorChip
                  key={i}
                  icon={s.icon}
                  color={s.color}
                  value={t(`${data}.${s.valueKey}`)}
                  label={t(`${data}.${s.labelKey}`)}
                />
              ))}
            </div>

            {/* CTA */}
            <button
              className="xAgro-network-cta"
              data-visible={visible}
              onClick={scrollToContact}
            >
              {cta}
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>

          </div>
        </div>
      </div>
    </section>
  );
};

export default withTranslation('common')(Network);